import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Minus, Plus, X, ShoppingBag, ArrowRight, Tag, Truck, CheckCircle, XCircle, Loader } from 'lucide-react';
import Header from '../components/Header';
import Footer from '../components/Footer';
import { useCart } from '../context/CartContext';
import { useAuth } from '../context/AuthContext';
import { toast } from 'sonner';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function CartPage() {
  const navigate = useNavigate();
  const { cart, updateQuantity, removeFromCart, loading } = useCart();
  const { user, token } = useAuth();

  const [couponCode, setCouponCode] = useState('');
  const [coupon, setCoupon] = useState(null);   // applied coupon object
  const [couponLoading, setCouponLoading] = useState(false);
  const [couponError, setCouponError] = useState('');

  const applyCoupon = async () => {
    if (!couponCode.trim()) return;
    setCouponLoading(true);
    setCouponError('');
    setCoupon(null);
    try {
      const res = await axios.post(`${API}/coupons/validate`,
        { code: couponCode.trim(), cart_total: cart.total },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setCoupon(res.data);
      toast.success(`Coupon applied: ${res.data.description}`);
    } catch (e) {
      const msg = e.response?.data?.detail || 'Invalid coupon code';
      setCouponError(msg);
      toast.error(msg);
    } finally {
      setCouponLoading(false);
    }
  };

  const removeCoupon = () => {
    setCoupon(null);
    setCouponCode('');
    setCouponError('');
  };

  const discount = coupon?.discount || 0;
  const freeShipping = coupon?.free_shipping || false;
  const shippingCost = (cart.total >= 200 || freeShipping) ? 0 : 25;
  const orderTotal = cart.total - discount + shippingCost;

  if (!user) {
    return (
      <div className="bg-background min-h-screen">
        <Header />
        <div className="pt-[105px] flex flex-col items-center justify-center min-h-[60vh] px-4">
          <ShoppingBag size={48} className="text-white/20 mb-4" />
          <h2 className="font-heading font-black text-2xl uppercase text-white mb-2">Login to View Cart</h2>
          <p className="text-muted-foreground text-sm mb-6">Sign in to access your cart and continue shopping.</p>
          <Link to="/auth" className="btn-primary flex items-center gap-2" data-testid="cart-login-btn">
            Login / Register <ArrowRight size={15} />
          </Link>
        </div>
        <Footer />
      </div>
    );
  }

  return (
    <div className="bg-background min-h-screen">
      <Header />
      <div className="pt-[105px]">
        {/* Page title */}
        <div className="bg-surface border-b border-white/5 py-5">
          <div className="container mx-auto px-4 md:px-6 max-w-7xl">
            <h1 className="font-heading font-black text-2xl md:text-3xl uppercase text-white">Shopping Cart</h1>
            <p className="text-muted-foreground text-sm mt-0.5">{cart.items.length} item{cart.items.length !== 1 ? 's' : ''}</p>
          </div>
        </div>

        <div className="container mx-auto px-4 md:px-6 max-w-7xl py-8">
          {loading ? (
            <div className="space-y-4">
              {[1,2,3].map(i => <div key={i} className="h-24 shimmer-bg" />)}
            </div>
          ) : cart.items.length === 0 ? (
            <div className="text-center py-20" data-testid="empty-cart">
              <ShoppingBag size={64} className="text-white/10 mx-auto mb-4" />
              <h2 className="font-heading font-black text-2xl uppercase text-white mb-2">Your Cart is Empty</h2>
              <p className="text-muted-foreground text-sm mb-6">Browse our tools and find what you need.</p>
              <Link to="/products" className="btn-primary inline-flex items-center gap-2" data-testid="empty-cart-shop-btn">
                Browse Products <ArrowRight size={15} />
              </Link>
            </div>
          ) : (
            <div className="grid lg:grid-cols-3 gap-8">
              {/* Cart items */}
              <div className="lg:col-span-2 space-y-3">
                {cart.items.map((item, i) => (
                  <div key={i} className="bg-surface border border-white/5 p-4 flex gap-4" data-testid={`cart-item-${item.product_id}`}>
                    {/* Image */}
                    <Link to={`/products/${item.product_id}`} className="flex-shrink-0">
                      <img
                        src={item.product?.images?.[0] || ''}
                        alt={item.product?.name}
                        className="w-20 h-20 object-cover"
                      />
                    </Link>

                    {/* Info */}
                    <div className="flex-1 min-w-0">
                      <div className="flex items-start justify-between gap-2">
                        <div>
                          <p className="text-xs font-bold uppercase text-primary">{item.product?.brand}</p>
                          <Link to={`/products/${item.product_id}`} className="text-sm font-semibold text-white hover:text-primary transition-colors line-clamp-2">
                            {item.product?.name}
                          </Link>
                          <p className="text-xs text-muted-foreground mt-0.5 font-mono">SKU: {item.product?.sku}</p>
                        </div>
                        <button
                          onClick={() => removeFromCart(item.product_id)}
                          className="text-white/30 hover:text-red-400 transition-colors flex-shrink-0"
                          data-testid={`remove-item-${item.product_id}`}
                        >
                          <X size={15} />
                        </button>
                      </div>

                      <div className="flex items-center justify-between mt-3">
                        {/* Quantity */}
                        <div className="flex items-center border border-white/10">
                          <button
                            onClick={() => updateQuantity(item.product_id, item.quantity - 1)}
                            className="w-8 h-8 flex items-center justify-center text-white hover:text-primary hover:bg-white/5 transition-colors"
                            data-testid={`qty-minus-${item.product_id}`}
                          >
                            <Minus size={12} />
                          </button>
                          <span className="w-10 text-center text-white font-mono text-sm" data-testid={`qty-display-${item.product_id}`}>
                            {item.quantity}
                          </span>
                          <button
                            onClick={() => updateQuantity(item.product_id, item.quantity + 1)}
                            className="w-8 h-8 flex items-center justify-center text-white hover:text-primary hover:bg-white/5 transition-colors"
                            data-testid={`qty-plus-${item.product_id}`}
                          >
                            <Plus size={12} />
                          </button>
                        </div>

                        {/* Price */}
                        <div className="text-right">
                          <div className="price-tag text-primary font-black text-base" data-testid={`item-subtotal-${item.product_id}`}>
                            AED {item.subtotal?.toLocaleString()}
                          </div>
                          <div className="text-xs text-muted-foreground">AED {item.product?.price} each</div>
                        </div>
                      </div>
                    </div>
                  </div>
                ))}

                {/* Continue shopping */}
                <Link to="/products" className="inline-flex items-center gap-2 text-sm text-muted-foreground hover:text-primary transition-colors mt-2">
                  <ArrowRight size={13} className="rotate-180" /> Continue Shopping
                </Link>
              </div>

              {/* Order summary */}
              <div className="lg:col-span-1">
                <div className="bg-surface border border-white/5 p-6 sticky top-24" data-testid="order-summary">
                  <h2 className="font-heading font-bold text-lg uppercase text-white mb-4">Order Summary</h2>

                  {/* Coupon */}
                  {coupon ? (
                    <div className="flex items-center justify-between bg-primary/10 border border-primary/30 px-3 py-2.5 mb-4">
                      <div className="flex items-center gap-2">
                        <CheckCircle size={14} className="text-primary flex-shrink-0" />
                        <div>
                          <p className="text-xs font-bold text-primary uppercase">{coupon.code}</p>
                          <p className="text-xs text-white/60">{coupon.description}</p>
                        </div>
                      </div>
                      <button onClick={removeCoupon} className="text-white/40 hover:text-red-400 transition-colors ml-2" data-testid="remove-coupon-btn">
                        <X size={14} />
                      </button>
                    </div>
                  ) : (
                    <div className="mb-4">
                      <div className="flex gap-2">
                        <input
                          type="text"
                          value={couponCode}
                          onChange={e => { setCouponCode(e.target.value.toUpperCase()); setCouponError(''); }}
                          onKeyDown={e => e.key === 'Enter' && applyCoupon()}
                          placeholder="Coupon code"
                          className="flex-1 bg-background border border-white/10 focus:border-primary text-white placeholder-white/30 text-xs px-3 py-2 outline-none transition-colors uppercase"
                          style={{ borderRadius: 0 }}
                          data-testid="coupon-input"
                        />
                        <button
                          onClick={applyCoupon}
                          disabled={couponLoading || !couponCode.trim()}
                          className="bg-white/5 border border-white/10 hover:border-primary text-white text-xs px-3 py-2 transition-colors font-bold disabled:opacity-50 flex items-center gap-1"
                          data-testid="apply-coupon-btn"
                        >
                          {couponLoading ? <Loader size={13} className="animate-spin" /> : <Tag size={13} />}
                        </button>
                      </div>
                      {couponError && (
                        <p className="text-xs text-red-400 mt-1 flex items-center gap-1" data-testid="coupon-error">
                          <XCircle size={11} /> {couponError}
                        </p>
                      )}
                    </div>
                  )}

                  {/* Summary lines */}
                  <div className="space-y-2 border-t border-white/10 pt-4 mb-4">
                    <div className="flex justify-between text-sm">
                      <span className="text-muted-foreground">Subtotal</span>
                      <span className="text-white font-mono" data-testid="cart-subtotal">AED {cart.total?.toLocaleString()}</span>
                    </div>
                    {discount > 0 && (
                      <div className="flex justify-between text-sm" data-testid="cart-discount">
                        <span className="text-green-400">Discount ({coupon.code})</span>
                        <span className="text-green-400 font-mono font-bold">− AED {discount.toFixed(0)}</span>
                      </div>
                    )}
                    {freeShipping && !discount && (
                      <div className="flex justify-between text-sm" data-testid="cart-free-shipping">
                        <span className="text-green-400">Shipping ({coupon.code})</span>
                        <span className="text-green-400 font-semibold">FREE</span>
                      </div>
                    )}
                    <div className="flex justify-between text-sm">
                      <span className="text-muted-foreground">Shipping</span>
                      {shippingCost === 0 ? (
                        <span className="text-green-400 font-semibold">FREE</span>
                      ) : (
                        <span className="text-white font-mono">AED {shippingCost}</span>
                      )}
                    </div>
                    {shippingCost > 0 && (
                      <p className="text-xs text-muted-foreground flex items-center gap-1">
                        <Truck size={11} /> Add AED {(200 - cart.total).toFixed(0)} more for free shipping
                      </p>
                    )}
                  </div>

                  <div className="flex justify-between items-center border-t border-white/20 pt-4 mb-6">
                    <span className="font-heading font-bold text-lg text-white uppercase">Total</span>
                    <span className="price-tag text-primary font-black text-xl" data-testid="cart-total">AED {Math.max(0, orderTotal).toFixed(0)}</span>
                  </div>

                  <button
                    onClick={() => navigate('/checkout', { state: { coupon } })}
                    className="w-full flex items-center justify-center gap-2 bg-primary text-black font-bold uppercase tracking-wider py-3 hover:bg-white transition-colors text-sm"
                    data-testid="proceed-checkout-btn"
                  >
                    Proceed to Checkout <ArrowRight size={15} />
                  </button>

                  <div className="mt-4 space-y-2">
                    {['Secure SSL checkout', '100% Genuine products', 'UAE warranty included'].map(t => (
                      <div key={t} className="flex items-center gap-2 text-xs text-muted-foreground">
                        <span className="text-primary">✓</span> {t}
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
      <Footer />
    </div>
  );
}
